/**
 * v3-parity G1 — running-total SSoT.
 *
 * A „MOST FIZETENDŐ" összeg egyetlen forrása: a felső perzisztens sáv
 * (WizardTopBar) és az alsó/rail summary (StickySummaryFooter) is ezt használja,
 * így a loyalty-levonás (post-VAT credit) minden lépésen konzisztens. A
 * grandTotal/számla-végösszeg VÁLTOZATLAN — a credit csak a fizetendőből vonódik.
 */
export interface RunningTotalParts {
  grandTotal: number;
  depositTotal: number;
  crossSellGross: number;
  consignmentTotal: number;
  loyaltyAmount: number;
}

export function computeTotalToPay(parts: RunningTotalParts): number {
  const gross =
    parts.grandTotal + parts.crossSellGross + parts.consignmentTotal + parts.depositTotal;
  return Math.max(0, gross - parts.loyaltyAmount);
}

/**
 * CONSIGNMENT package-tételek összege (qty × unitPrice; a per-tétel mennyiség-override
 * a consignmentQuantities-ből, fallback az item.quantity). A WizardTopBar +
 * StickySummaryFooter + SummaryStep KÖZÖS forrása → a „MOST FIZETENDŐ" consignment-része
 * nem divergálhat (korábban a TopBar 0-stubot adott, míg a Footer valódian számolt).
 */
export interface ConsignmentClassifiedItem {
  id: string;
  settlementType: string;
  quantity: number;
  unitPrice?: number | null;
}

export function computeConsignmentTotal(
  items: ConsignmentClassifiedItem[] | undefined,
  consignmentQuantities: Map<string, number> | undefined
): number {
  return (items ?? [])
    .filter(i => i.settlementType === 'CONSIGNMENT')
    .reduce((sum, item) => {
      const qty = consignmentQuantities?.get(item.id) ?? item.quantity;
      return sum + qty * (item.unitPrice ?? 0);
    }, 0);
}

/**
 * Cross-sell termékek nettó / ÁFA / bruttó összege (Σ lineTotal + 27% ÁFA kerekítve).
 * A WizardTopBar + StickySummaryFooter + SummaryStep KÖZÖS forrása → a cross-sell bruttó
 * nem divergálhat. Korábban a TopBar/Footer `round(net×1.27)`-et, a SummaryStep
 * `net + round(net×0.27)`-et számolt inline; egész HUF nettón matematikailag azonos
 * (round(net×1.27) === net + round(net×0.27), mert net egész), de a divergencia-kockázat
 * (tört-nettó / formula-elcsúszás) ellen ez a kanonikus, számla-tételes (ÁFA-explicit) forma.
 */
export interface CrossSellGrossItem {
  lineTotal: number;
}

export interface CrossSellGrossResult {
  net: number;
  vat: number;
  gross: number;
}

export function computeCrossSellGross(
  items: CrossSellGrossItem[] | undefined
): CrossSellGrossResult {
  const net = (items ?? []).reduce((sum, item) => sum + item.lineTotal, 0);
  const vat = Math.round(net * 0.27);
  return { net, vat, gross: net + vat };
}
